import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useReducer, useRef, useCallback, useEffect, useImperativeHandle, forwardRef, useState, } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { imageViewerReducer, createInitialState } from './imageViewerReducer';
import { useImageZoom } from './hooks/useImageZoom';
import { useImagePan } from './hooks/useImagePan';
import { createPortal } from 'react-dom';
import { FullscreenButton } from '../player/components/FullscreenButton';
import { SidePanel } from '../player/components/SidePanel';
import { DownloadOverlay } from '../player/components/DownloadOverlay';
import { AnnotationEditor } from '../annotations/AnnotationEditor';
import { AnnotationViewer } from '../annotations/AnnotationViewer';
import { requestFullscreen, exitFullscreen as exitFs, getFullscreenElement, addFullscreenChangeListener, } from '../player/utils/fullscreenCompat';
const IMAGE_VIEWER_KEYBOARD_SHORTCUTS = [
    { key: '+', description: 'Zoom in' },
    { key: '-', description: 'Zoom out' },
    { key: '0', description: 'Zoom to fit' },
    { key: '1', description: 'Zoom to 100%' },
    { key: 'Shift + F', description: 'Toggle fullscreen' },
    { key: '←', description: 'Pan left' },
    { key: '→', description: 'Pan right' },
    { key: '↑', description: 'Pan up' },
    { key: '↓', description: 'Pan down' },
];
const FULLSCREEN_CLASS = 'hekate-player-fullscreen-wrapper';
const HekateImageViewer = forwardRef(function HekateImageViewer({ imageElementId, containerElement, onFullscreenChange, canDownload, downloadUrl, fileTitle, fileTypeName, fileSize, fileIconUrl, sidePanelElementId, sidePanelWidth: sidePanelWidthProp, sidePanelTooltip, onMounted, isActive: isActiveProp = true, }, ref) {
    const [state, dispatch] = useReducer(imageViewerReducer, undefined, createInitialState);
    const stateRef = useRef(state);
    stateRef.current = state;
    const containerRef = useRef(null);
    const viewportRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
    const [annotationEditing, setAnnotationEditing] = useState(false);
    // Track mouse position over the viewport so keyboard zoom can zoom
    // toward the cursor (matching scroll-wheel behavior).
    // null = mouse is not over the viewport.
    const mouseOverViewport = useRef(null);
    const [errorMessage, setErrorMessage] = useState(null);
    // Pan/zoom sync subscribers (same pattern as video player's subscribersRef)
    const viewSubscribersRef = useRef([]);
    const isFirstViewNotifyRef = useRef(true);
    // Side panel refs
    const sidePanelContentRef = useRef(null);
    const sidePanelAdoptedElRef = useRef(null);
    const sidePanelElementOriginRef = useRef(null);
    const getState = useCallback(() => stateRef.current, []);
    // Adopt the existing <img> element into the canvas
    useEffect(() => {
        const img = document.getElementById(imageElementId);
        if (!img || !canvasRef.current)
            return;
        imgRef.current = img;
        // Move img into canvas
        canvasRef.current.prepend(img);
        img.style.display = 'block';
        img.style.maxWidth = 'none';
        img.style.maxHeight = 'none';
        img.draggable = false;
        const handleLoad = () => {
            setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
        };
        const handleError = () => {
            setErrorMessage('This file could not be displayed.');
        };
        if (img.complete && img.naturalWidth > 0) {
            handleLoad();
        }
        else {
            img.addEventListener('load', handleLoad, { once: true });
            img.addEventListener('error', handleError, { once: true });
        }
        return () => {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
        };
    }, [imageElementId]);
    // Compute fitZoom whenever image or viewport size changes
    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp || imgSize.w === 0)
            return;
        const computeFit = () => {
            const vpRect = vp.getBoundingClientRect();
            const fitZoom = Math.min(vpRect.width / imgSize.w, vpRect.height / imgSize.h);
            const centerX = (vpRect.width - imgSize.w * fitZoom) / 2;
            const centerY = (vpRect.height - imgSize.h * fitZoom) / 2;
            dispatch({ type: 'SET_FIT_ZOOM', fitZoom, centerX, centerY });
        };
        computeFit();
        const ro = new ResizeObserver(computeFit);
        ro.observe(vp);
        return () => ro.disconnect();
    }, [imgSize]);
    // Update image dimensions in the canvas
    useEffect(() => {
        const img = imgRef.current;
        if (!img || imgSize.w === 0)
            return;
        img.style.width = imgSize.w + 'px';
        img.style.height = imgSize.h + 'px';
    }, [imgSize]);
    // Sync externally-mounted annotation roots with the image position/size.
    // External code (annotations2.js) mounts annotation viewers as direct children
    // of the wrapper (containerElement), not inside the canvas. Position and size
    // the root to match the image's visual area (no CSS scale — keeps stroke widths
    // correct via vectorEffect="non-scaling-stroke").
    const syncAnnotationRootsImpl = useCallback(() => {
        if (!containerElement || imgSize.w === 0)
            return;
        const s = stateRef.current;
        // panX/panY are relative to the viewport (where the canvas lives),
        // but annotation roots are direct children of containerElement.
        // When the annotation toolbar is open, the viewport shifts down by
        // the toolbar height. Account for this offset so the annotation
        // root aligns with the canvas/image, not the wrapper top.
        const vp = viewportRef.current;
        const vpOffset = vp
            ? vp.getBoundingClientRect().top - containerElement.getBoundingClientRect().top
            : 0;
        const roots = containerElement.querySelectorAll(':scope > [data-annotation-root]');
        roots.forEach((el) => {
            const htmlEl = el;
            htmlEl.style.left = s.panX + 'px';
            htmlEl.style.top = (s.panY + vpOffset) + 'px';
            htmlEl.style.width = (imgSize.w * s.zoom) + 'px';
            htmlEl.style.height = (imgSize.h * s.zoom) + 'px';
            // Set data-zoom so text fontSize scales correctly and
            // SelectionOutline can scale handles inversely.
            htmlEl.dataset.zoom = String(s.zoom);
        });
        // Also update annotation roots inside the canvas (editor/viewer).
        const canvasRoots = canvasRef.current?.querySelectorAll('[data-annotation-root]');
        canvasRoots?.forEach((el) => {
            el.dataset.zoom = String(s.zoom);
        });
    }, [containerElement, imgSize]);
    // syncAnnotationRoots runs the impl immediately AND schedules
    // deferred re-runs to catch the toolbar portal rendering.
    // The MutationObserver fires when the editor root is added (before
    // the toolbar renders), so the initial vpOffset is 0. The deferred
    // runs pick up the toolbar's effect on viewport offset.
    const syncTimerRef = useRef(null);
    const syncAnnotationRoots = useCallback(() => {
        syncAnnotationRootsImpl();
        // Schedule follow-up syncs to catch toolbar rendering
        if (syncTimerRef.current)
            clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
            syncAnnotationRootsImpl();
            syncTimerRef.current = setTimeout(syncAnnotationRootsImpl, 100);
        }, 50);
    }, [syncAnnotationRootsImpl]);
    // Apply transform to canvas + sync annotation roots on zoom/pan changes
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        canvas.style.transform = `translate(${state.panX}px, ${state.panY}px) scale(${state.zoom})`;
        syncAnnotationRoots();
    }, [state.zoom, state.panX, state.panY, syncAnnotationRoots]);
    // Notify view subscribers on zoom/pan changes (skip first render)
    useEffect(() => {
        if (isFirstViewNotifyRef.current) {
            isFirstViewNotifyRef.current = false;
            return;
        }
        const event = {
            type: 'zoom',
            zoom: state.zoom,
            panX: state.panX,
            panY: state.panY,
        };
        for (const sub of viewSubscribersRef.current) {
            sub(event);
        }
    }, [state.zoom, state.panX, state.panY]);
    // Watch for children added/removed in containerElement and re-sync.
    // This covers both annotation root additions AND the toolbar appearing
    // (which shifts the viewport down and changes the vpOffset calculation).
    // Also detects external annotation editor mounts (mountAnnotationEditor)
    // to set annotationEditing — this disables pan/zoom keyboard shortcuts
    // so arrow keys can nudge shapes instead of panning the image.
    useEffect(() => {
        if (!containerElement)
            return;
        const observer = new MutationObserver(() => {
            syncAnnotationRoots();
            const hasEditor = !!containerElement.querySelector('[data-annotation-root="editor"]');
            setAnnotationEditing(hasEditor);
        });
        observer.observe(containerElement, { childList: true });
        syncAnnotationRoots();
        return () => observer.disconnect();
    }, [containerElement, syncAnnotationRoots]);
    // Set --video-ar on the canvas for annotation overlay CSS
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || imgSize.w === 0)
            return;
        canvas.style.setProperty('--video-ar', `${imgSize.w} / ${imgSize.h}`);
    }, [imgSize]);
    // Fullscreen handling
    useEffect(() => {
        const handleChange = () => {
            const isFs = !!getFullscreenElement();
            dispatch(isFs ? { type: 'ENTER_FULLSCREEN' } : { type: 'EXIT_FULLSCREEN' });
            onFullscreenChange?.(isFs);
            const el = containerElement ?? containerRef.current;
            if (el) {
                if (isFs) {
                    el.classList.add(FULLSCREEN_CLASS);
                }
                else {
                    el.classList.remove(FULLSCREEN_CLASS);
                }
            }
        };
        const cleanup = addFullscreenChangeListener(handleChange);
        return () => {
            cleanup();
            (containerElement ?? containerRef.current)?.classList.remove(FULLSCREEN_CLASS);
        };
    }, [onFullscreenChange, containerElement]);
    // Restore side panel element and close panel when leaving fullscreen
    useEffect(() => {
        if (!state.isFullscreen) {
            dispatch({ type: 'SET_SIDE_PANEL', open: false });
            const origin = sidePanelElementOriginRef.current;
            const el = sidePanelAdoptedElRef.current;
            if (origin && el) {
                if (origin.nextSibling) {
                    origin.parent.insertBefore(el, origin.nextSibling);
                }
                else {
                    origin.parent.appendChild(el);
                }
                sidePanelElementOriginRef.current = null;
                sidePanelAdoptedElRef.current = null;
            }
        }
    }, [state.isFullscreen]);
    // Sync sidePanelWidth prop to state
    useEffect(() => {
        if (sidePanelWidthProp != null && sidePanelWidthProp > 0) {
            dispatch({ type: 'SET_SIDE_PANEL_WIDTH', width: sidePanelWidthProp });
        }
    }, [sidePanelWidthProp]);
    // Toggle wrapper class to reserve space for the side panel when open
    useEffect(() => {
        const el = containerElement ?? containerRef.current;
        if (!el)
            return;
        el.style.setProperty('--side-panel-w', `${state.sidePanelWidth}px`);
        if (state.isFullscreen && state.sidePanelOpen) {
            el.classList.add('hekate-player-sidepanel-active');
        }
        else {
            el.classList.remove('hekate-player-sidepanel-active');
        }
    }, [state.isFullscreen, state.sidePanelOpen, state.sidePanelWidth, containerElement]);
    // Helper: adopt a DOM element into the side panel (or restore the current one)
    const adoptSidePanelElement = useCallback((elementId) => {
        const panel = sidePanelContentRef.current;
        // Restore any previously adopted element first
        const origin = sidePanelElementOriginRef.current;
        const adopted = sidePanelAdoptedElRef.current;
        if (origin && adopted) {
            if (origin.nextSibling) {
                origin.parent.insertBefore(adopted, origin.nextSibling);
            }
            else {
                origin.parent.appendChild(adopted);
            }
            sidePanelElementOriginRef.current = null;
            sidePanelAdoptedElRef.current = null;
        }
        if (!elementId) {
            dispatch({ type: 'SET_SIDE_PANEL_HAS_ELEMENT', hasElement: false });
            return;
        }
        dispatch({ type: 'SET_SIDE_PANEL_HAS_ELEMENT', hasElement: true });
        if (!panel)
            return;
        const el = document.getElementById(elementId);
        if (!el || !el.parentElement)
            return;
        if (el.parentElement === panel)
            return;
        sidePanelElementOriginRef.current = {
            parent: el.parentElement,
            nextSibling: el.nextSibling,
        };
        sidePanelAdoptedElRef.current = el;
        panel.appendChild(el);
    }, []);
    // Sync sidePanelElementId prop — adopt when entering fullscreen
    useEffect(() => {
        if (state.isFullscreen) {
            adoptSidePanelElement(sidePanelElementId ?? null);
        }
    }, [sidePanelElementId, state.isFullscreen, adoptSidePanelElement]);
    // Zoom/pan hooks
    // Disable zoom on inactive image viewers only when sync-position is
    // on AND the active column is also an image viewer (so the zoom
    // would interfere with synced pan/zoom). Otherwise, allow independent zoom.
    const shouldDisableInactiveZoom = !isActiveProp && (() => {
        try {
            if (localStorage.getItem('player.sync.position') === 'false')
                return false;
        }
        catch { /* ignore */ }
        const pc = window.playerColumns;
        if (!pc || typeof pc.getActiveColumnPlayer !== 'function')
            return false;
        const activePlayer = pc.getActiveColumnPlayer();
        return activePlayer != null && activePlayer.type === 'image';
    })();
    const zoomDisabled = annotationEditing || shouldDisableInactiveZoom;
    const zoomHandlers = useImageZoom({
        dispatch,
        getState,
        viewportRef: viewportRef,
        disabled: zoomDisabled,
    });
    // Native wheel listener — React adds wheel as passive, so preventDefault doesn't work.
    // Attach a non-passive native listener to prevent page scrolling during zoom.
    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp)
            return;
        const handler = (e) => {
            if (zoomDisabled)
                return;
            e.preventDefault();
            const state = stateRef.current;
            const factor = e.deltaY > 0 ? 0.9 : 1.1;
            const newZoom = state.zoom * factor;
            // Clamp here to avoid dispatching when already at min/max zoom
            const clamped = Math.min(10, Math.max(0.01, newZoom));
            if (Math.abs(clamped - state.zoom) < 1e-6)
                return;
            zoomHandlers.zoomToward(e.clientX, e.clientY, clamped);
        };
        vp.addEventListener('wheel', handler, { passive: false });
        return () => vp.removeEventListener('wheel', handler);
    }, [zoomDisabled, zoomHandlers]);
    const panHandlers = useImagePan({
        dispatch,
        getState,
        disabled: annotationEditing,
    });
    // Keyboard shortcuts
    useEffect(() => {
        const ZOOM_STEP = 1.25;
        const handleKey = (e) => {
            // Don't capture when inside an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')
                return;
            if (annotationEditing)
                return;
            switch (e.key) {
                case '+':
                case '=': {
                    e.preventDefault();
                    const s = stateRef.current;
                    const newZoom = Math.min(10, s.zoom * ZOOM_STEP);
                    if (mouseOverViewport.current) {
                        zoomHandlers.zoomToward(mouseOverViewport.current.x, mouseOverViewport.current.y, newZoom);
                    }
                    else {
                        dispatch({ type: 'ZOOM_IN' });
                    }
                    break;
                }
                case '-': {
                    e.preventDefault();
                    const s = stateRef.current;
                    const newZoom = Math.max(0.01, s.zoom / ZOOM_STEP);
                    if (mouseOverViewport.current) {
                        zoomHandlers.zoomToward(mouseOverViewport.current.x, mouseOverViewport.current.y, newZoom);
                    }
                    else {
                        dispatch({ type: 'ZOOM_OUT' });
                    }
                    break;
                }
                case '0':
                    e.preventDefault();
                    handleZoomToFit();
                    break;
                case '1':
                    e.preventDefault();
                    handleZoomTo100();
                    break;
                case 'f':
                case 'F':
                    if (e.shiftKey) {
                        e.preventDefault();
                        toggleFullscreen();
                    }
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    dispatch({ type: 'PAN', panX: state.panX + 50, panY: state.panY });
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    dispatch({ type: 'PAN', panX: state.panX - 50, panY: state.panY });
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    dispatch({ type: 'PAN', panX: state.panX, panY: state.panY + 50 });
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    dispatch({ type: 'PAN', panX: state.panX, panY: state.panY - 50 });
                    break;
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [annotationEditing, state.panX, state.panY, zoomHandlers]);
    const toggleFullscreen = useCallback(() => {
        const el = containerElement ?? containerRef.current;
        if (!el)
            return;
        if (getFullscreenElement()) {
            exitFs();
        }
        else {
            requestFullscreen(el);
        }
    }, [containerElement]);
    const handleZoomToFit = useCallback(() => {
        const vp = viewportRef.current;
        if (!vp || imgSize.w === 0)
            return;
        const vpRect = vp.getBoundingClientRect();
        const fitZoom = Math.min(vpRect.width / imgSize.w, vpRect.height / imgSize.h);
        const centerX = (vpRect.width - imgSize.w * fitZoom) / 2;
        const centerY = (vpRect.height - imgSize.h * fitZoom) / 2;
        dispatch({ type: 'SET_FIT_ZOOM', fitZoom, centerX, centerY, force: true });
    }, [imgSize]);
    const handleZoomTo100 = useCallback(() => {
        const vp = viewportRef.current;
        if (!vp || imgSize.w === 0)
            return;
        const vpRect = vp.getBoundingClientRect();
        // Center the image at 100%
        const panX = (vpRect.width - imgSize.w) / 2;
        const panY = (vpRect.height - imgSize.h) / 2;
        dispatch({ type: 'SET_ZOOM', zoom: 1, panX, panY });
    }, [imgSize]);
    // Annotation integration
    const showAnnotationEditor = useCallback((options = {}) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return { unmount: () => { }, get api() { return null; } };
        }
        const wrapper = document.createElement('div');
        wrapper.setAttribute('data-annotation-root', 'editor');
        canvas.appendChild(wrapper);
        canvas.classList.add('hekate-annotation-active');
        setAnnotationEditing(true);
        const targetRefObj = { current: wrapper };
        const apiRefObj = React.createRef();
        const root = createRoot(wrapper);
        const { initialShapes, isEditing, onSave, onDiscard, isFullscreen, defaultColor } = options;
        // The annotation root lives inside the canvas (which is CSS-
        // transformed for zoom/pan), but the toolbar must NOT inherit
        // that transform — otherwise zooming a very large image down
        // to fit shrinks the toolbar with it, parking it at a tiny
        // size near the canvas's transformed top-left. Portal the
        // toolbar to the wrapper instead, where it becomes a flex
        // child rendered above the image at full size.
        const toolbarPortalTarget = containerElement ?? containerRef.current;
        flushSync(() => {
            root.render(React.createElement(AnnotationEditor, {
                ref: apiRefObj,
                targetRef: targetRefObj,
                initialShapes,
                isEditing,
                onSave,
                onDiscard,
                isFullscreen,
                defaultColor,
                toolbarPortalTarget,
            }));
        });
        return {
            unmount: () => {
                root.unmount();
                wrapper.remove();
                canvas.classList.remove('hekate-annotation-active');
                setAnnotationEditing(false);
            },
            get api() {
                return apiRefObj.current;
            },
        };
    }, []);
    const showAnnotationViewer = useCallback((shapes) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return {
                unmount: () => { },
                get api() { return null; },
                updateShapes: () => { },
            };
        }
        const wrapper = document.createElement('div');
        wrapper.setAttribute('data-annotation-root', 'viewer');
        canvas.appendChild(wrapper);
        canvas.classList.add('hekate-annotation-active');
        const targetRefObj = { current: wrapper };
        const apiRefObj = React.createRef();
        const root = createRoot(wrapper);
        function renderViewer(currentShapes) {
            flushSync(() => {
                root.render(React.createElement(AnnotationViewer, {
                    ref: apiRefObj,
                    targetRef: targetRefObj,
                    shapes: currentShapes,
                }));
            });
        }
        renderViewer(shapes);
        const handle = {
            unmount: () => {
                root.unmount();
                wrapper.remove();
                canvas.classList.remove('hekate-annotation-active');
            },
            get api() {
                return apiRefObj.current;
            },
            updateShapes: (newShapes) => {
                renderViewer(newShapes);
            },
        };
        return handle;
    }, []);
    // Imperative API
    useImperativeHandle(ref, () => ({
        zoomIn: () => dispatch({ type: 'ZOOM_IN' }),
        zoomOut: () => dispatch({ type: 'ZOOM_OUT' }),
        zoomToFit: handleZoomToFit,
        zoomTo100: handleZoomTo100,
        getZoom: () => stateRef.current.zoom,
        isFullscreen: () => stateRef.current.isFullscreen,
        enterFullscreen: () => {
            const el = containerElement ?? containerRef.current;
            if (el && !getFullscreenElement()) {
                requestFullscreen(el);
            }
        },
        exitFullscreen: () => {
            if (getFullscreenElement())
                exitFs();
        },
        showAnnotationEditor,
        showAnnotationViewer,
        setSidePanelOpen: (open) => { dispatch({ type: 'SET_SIDE_PANEL', open }); },
        setSidePanelElement: (elementId) => {
            adoptSidePanelElement(elementId);
        },
        setSidePanelWidth: (width) => {
            dispatch({ type: 'SET_SIDE_PANEL_WIDTH', width });
        },
        focus: (options) => { containerRef.current?.focus(options); },
        getContainerElement: () => containerElement ?? containerRef.current,
        getKeyboardShortcuts: () => IMAGE_VIEWER_KEYBOARD_SHORTCUTS,
        getViewState: () => ({ zoom: stateRef.current.zoom, panX: stateRef.current.panX, panY: stateRef.current.panY }),
        setViewState: (zoom, panX, panY) => {
            dispatch({ type: 'SET_ZOOM', zoom, panX, panY });
        },
        subscribeToViewEvents: (cb) => {
            viewSubscribersRef.current = [...viewSubscribersRef.current, cb];
            return { unsubscribe: () => { viewSubscribersRef.current = viewSubscribersRef.current.filter(item => item !== cb); } };
        },
    }), [handleZoomToFit, handleZoomTo100, showAnnotationEditor, showAnnotationViewer, adoptSidePanelElement]);
    // Fire onMounted callback once after the first render commit.
    useEffect(() => { onMounted?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Unmount cleanup — restore any adopted side panel element (e.g. #comment-tabs)
    // to its original DOM position BEFORE React tears down the portal that
    // currently contains it. See the matching comment in HekatePlayer.tsx for
    // the full rationale.
    useEffect(() => {
        return () => {
            const origin = sidePanelElementOriginRef.current;
            const el = sidePanelAdoptedElRef.current;
            if (origin && el) {
                if (origin.nextSibling && origin.nextSibling.parentNode === origin.parent) {
                    origin.parent.insertBefore(el, origin.nextSibling);
                }
                else {
                    origin.parent.appendChild(el);
                }
                sidePanelElementOriginRef.current = null;
                sidePanelAdoptedElRef.current = null;
            }
        };
    }, []);
    const zoomPercent = Math.round(state.zoom * 100);
    const hasError = errorMessage !== null;
    return (_jsxs("div", { ref: containerRef, className: "hekate-image-viewer", tabIndex: -1, children: [state.isFullscreen && !hasError && (containerElement ?? containerRef.current) && createPortal(_jsx(SidePanel, { isOpen: state.sidePanelOpen, onToggle: () => dispatch({ type: 'TOGGLE_SIDE_PANEL' }), controlsVisible: state.controlsVisible, contentRef: sidePanelContentRef, hasElement: state.sidePanelHasElement, tooltip: sidePanelTooltip }), (containerElement ?? containerRef.current)), hasError && (_jsx(DownloadOverlay, { visible: true, message: errorMessage, canDownload: !!canDownload, downloadUrl: downloadUrl, title: fileTitle, fileTypeName: fileTypeName, fileSize: fileSize, fileIconUrl: fileIconUrl })), !hasError && (_jsx("div", { ref: viewportRef, className: "hekate-image-viewer-viewport", onTouchStart: zoomHandlers.onTouchStart, onTouchMove: zoomHandlers.onTouchMove, onTouchEnd: zoomHandlers.onTouchEnd, onDoubleClick: zoomHandlers.onDoubleClick, onPointerDown: panHandlers.onPointerDown, onPointerMove: (e) => {
                    panHandlers.onPointerMove(e);
                    mouseOverViewport.current = { x: e.clientX, y: e.clientY };
                }, onPointerUp: panHandlers.onPointerUp, onMouseLeave: () => { mouseOverViewport.current = null; }, style: { cursor: annotationEditing ? undefined : 'grab' }, children: _jsx("div", { ref: canvasRef, className: "hekate-image-viewer-canvas", style: {
                        width: imgSize.w || undefined,
                        height: imgSize.h || undefined,
                    } }) })), !hasError && _jsx("div", { className: "hekate-player-controls", role: "toolbar", "aria-label": "Image controls", children: _jsxs("div", { className: "hekate-player-controls-bottom", children: [_jsx("button", { className: "hekate-player-btn hekate-image-zoom-fit-btn", "aria-label": "Zoom to fit", "data-tooltip": "Zoom to fit", onClick: handleZoomToFit, type: "button", children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsx("button", { className: "hekate-player-btn hekate-image-zoom-out-btn", "aria-label": "Zoom out", "data-tooltip": "Zoom out", onClick: () => dispatch({ type: 'ZOOM_OUT' }), type: "button", children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsxs("span", { className: "hekate-image-zoom-label", "aria-live": "polite", children: [zoomPercent, "%"] }), _jsx("button", { className: "hekate-player-btn hekate-image-zoom-in-btn", "aria-label": "Zoom in", "data-tooltip": "Zoom in", onClick: () => dispatch({ type: 'ZOOM_IN' }), type: "button", children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsx("button", { className: "hekate-player-btn hekate-image-zoom-100-btn", "aria-label": "Zoom to 100%", "data-tooltip": "Zoom to 100%", onClick: handleZoomTo100, type: "button", children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsx("div", { className: "spacer" }), _jsx(FullscreenButton, { isFullscreen: state.isFullscreen, onClick: toggleFullscreen })] }) })] }));
});
export default HekateImageViewer;
